import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import TablePagination from "@mui/material/TablePagination";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Fade from "@mui/material/Fade";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import AssessmentIcon from "@mui/icons-material/Assessment";
import HomeIcon from "@mui/icons-material/Home";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { motion, AnimatePresence } from "framer-motion";
import { useSnackbar } from "notistack";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getGovernmentBenefitsByStoreApi,
  exportGovernmentStoreApi,
} from "../../../api/government";
import MbsModeDialog from "./dialog/MbsModeDialog";

const StoreBenefits = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const searchParams = new URLSearchParams(location.search);
  const initialGroup = searchParams.get("group") || "";
  const initialMonth = searchParams.get("month") || "";

  const [groupName, setGroupName] = useState(initialGroup);
  const [selectedMonthParam, setSelectedMonthParam] = useState(initialMonth);
  const [storeSummary, setStoreSummary] = useState({});
  const [storeCount, setStoreCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [mbsModeDialogOpen, setMbsModeDialogOpen] = useState(false);
  const [pendingExportStore, setPendingExportStore] = useState("");

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "number") {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    const num = Number(value);
    if (!Number.isNaN(num)) {
      return num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return value;
  };

  const handleBackToGroups = () => {
    navigate("/reports/government-benefits");
  };

  const handleViewPersonnel = (store) => {
    navigate(
      `/reports/government-benefits/personnel?group=${encodeURIComponent(
        groupName
      )}&store=${encodeURIComponent(store)}&month=${encodeURIComponent(
        selectedMonthParam || ""
      )}`
    );
  };

  const performExportStore = async (store, mbsMode) => {
    if (!selectedMonthParam) {
      enqueueSnackbar("Missing month parameter.", { variant: "warning" });
      return;
    }
    try {
      const res = await exportGovernmentStoreApi({
        group: groupName,
        store,
        month: selectedMonthParam,
        mbsMode,
      });

      const disposition = res.headers["content-disposition"];
      let filename = `store_${store}_${selectedMonthParam}.csv`;

      if (disposition && disposition.indexOf("filename=") !== -1) {
        const matches = disposition.match(/filename="?(.+?)"?$/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar(`Exported store ${store}`, { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Failed to export store report", { variant: "error" });
    }
  };

  const handleExportStore = (store) => {
    if (!selectedMonthParam) {
      enqueueSnackbar("Missing month parameter.", { variant: "warning" });
      return;
    }
    setPendingExportStore(store);
    setMbsModeDialogOpen(true);
  };

  const closeMbsModeDialog = () => {
    setMbsModeDialogOpen(false);
    setPendingExportStore("");
  };

  const handleMbsModeSelect = async (mbsMode) => {
    const store = pendingExportStore;
    closeMbsModeDialog();
    if (store) await performExportStore(store, mbsMode);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!initialGroup || !initialMonth) {
        setError("Missing group or month parameter in URL.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const json = await getGovernmentBenefitsByStoreApi({
          group: initialGroup,
          month: initialMonth,
        });

        console.log("StoreBenefits raw response:", json);

        let data = json;
        if (data && typeof data === "object" && data.data) {
          data = data.data;
        }
        if (data && typeof data === "object" && data.data) {
          data = data.data;
        }
        if (data && data.status && data.data) {
          data = data.data;
        }

        setGroupName(data.groupName || initialGroup);
        setSelectedMonthParam(data.month || initialMonth);
        const summary = data.summary || {};
        setStoreSummary(summary);
        const countFromApi =
          typeof data.storeCount === "number"
            ? data.storeCount
            : Object.keys(summary).length;
        setStoreCount(countFromApi);

        enqueueSnackbar("Store-level government benefits loaded", {
          variant: "success",
        });
      } catch (err) {
        console.error("Error loading store-level government benefits:", err);
        setError("Failed to load store-level government benefits.");
        setStoreSummary({});
        setStoreCount(0);
        enqueueSnackbar("Failed to load store-level government benefits.", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [initialGroup, initialMonth, enqueueSnackbar]);

  const rows = useMemo(
    () =>
      Object.entries(storeSummary).map(([store, data]) => ({
        store,
        personnel_count: data.personnel_count || 0,
        first_date_range: data.first_date_range || "",
        second_date_range: data.second_date_range || "",
        sss: data.sss || {},
        philhealth: data.philhealth || data.phic || {},
        pagibig: data.pagibig || {},
      })),
    [storeSummary]
  );

  const paginatedRows = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const totals = useMemo(() => {
    const t = {
      sss_cutoff1: 0,
      sss_cutoff1_er: 0,
      sss_cutoff2: 0,
      sss_cutoff2_er: 0,
      sss_overall: 0,
      sss_overall_er: 0,
      ph_cutoff1: 0,
      ph_cutoff1_er: 0,
      ph_cutoff2: 0,
      ph_cutoff2_er: 0,
      ph_overall: 0,
      ph_overall_er: 0,
      pi_cutoff1: 0,
      pi_cutoff2: 0,
      pi_overall: 0,
    };

    rows.forEach((row) => {
      const sss = row.sss || {};
      const ph = row.philhealth || {};
      const pi = row.pagibig || {};

      t.sss_cutoff1 += Number(sss.cutoff1 || 0);
      t.sss_cutoff1_er += Number(sss.cutoff1_er || 0);
      t.sss_cutoff2 += Number(sss.cutoff2 || 0);
      t.sss_cutoff2_er += Number(sss.cutoff2_er || 0);
      t.sss_overall += Number(sss.overall || 0);
      t.sss_overall_er += Number(sss.overall_er || 0);

      t.ph_cutoff1 += Number(ph.cutoff1 || 0);
      t.ph_cutoff1_er += Number(ph.cutoff1_er || 0);
      t.ph_cutoff2 += Number(ph.cutoff2 || 0);
      t.ph_cutoff2_er += Number(ph.cutoff2_er || 0);
      t.ph_overall += Number(ph.overall || 0);
      t.ph_overall_er += Number(ph.overall_er || 0);

      t.pi_cutoff1 += Number(pi.cutoff1 || 0);
      t.pi_cutoff2 += Number(pi.cutoff2 || 0);
      t.pi_overall += Number(pi.overall || 0);
    });

    return t;
  }, [rows]);

  const headCellBaseSx = {
    bgcolor: "#f8f9fa",
    fontWeight: "bold",
    borderBottom: "2px solid #e5e7eb",
    borderRight: "1px solid #e0e0e0",
    textAlign: "center",
    whiteSpace: "nowrap",
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              mb: 2,
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={handleBackToGroups}>
                <ArrowBackIcon />
              </IconButton>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "#2c3e50",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <AssessmentIcon sx={{ color: "#12C1DC" }} />
                Store Government Benefits
              </Typography>
            </Box>
            <Chip
              label={`${storeCount} Stores`}
              color="info"
              variant="outlined"
            />
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link underline="hover" color="inherit" href="/">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="/reports">
              Reports
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="/reports/government-benefits"
            >
              Government Benefits Summary
            </Link>
            <Typography color="text.primary">Stores</Typography>
          </Breadcrumbs>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Group Information
                  </Typography>
                  <Typography variant="body1">
                    <strong>Group:</strong> {groupName}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Store Count:</strong> {storeCount}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Month:</strong> {selectedMonthParam || "-"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Paper
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                p: 2,
                bgcolor: "#f9fafb",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <HomeIcon fontSize="small" />
                Store Summary
              </Typography>
            </Box>

            <Box sx={{ p: 3 }}>
              <TableContainer
                sx={{
                  maxHeight: "70vh",
                  overflowX: "auto",
                  overflowY: "auto",
                }}
              >
                <Table
                  stickyHeader
                  size={isMobile ? "small" : "medium"}
                  aria-label="store government benefits summary table"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        rowSpan={2}
                        sx={{ ...headCellBaseSx, minWidth: 150 }}
                      >
                        Actions
                      </TableCell>
                      <TableCell
                        rowSpan={2}
                        sx={{ ...headCellBaseSx, minWidth: 160 }}
                      >
                        Store
                      </TableCell>
                      <TableCell
                        rowSpan={2}
                        sx={{ ...headCellBaseSx, minWidth: 140 }}
                      >
                        # of Personnel
                      </TableCell>
                      <TableCell
                        rowSpan={2}
                        sx={{ ...headCellBaseSx, minWidth: 160 }}
                      >
                        1st Date Range
                      </TableCell>
                      <TableCell
                        rowSpan={2}
                        sx={{ ...headCellBaseSx, minWidth: 160 }}
                      >
                        2nd Date Range
                      </TableCell>
                      <TableCell
                        align="center"
                        colSpan={4}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 6 * 120,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <AccountBalanceIcon fontSize="small" />
                          SSS
                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                        colSpan={6}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 6 * 120,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <LocalHospitalIcon fontSize="small" />
                          PhilHealth
                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                        colSpan={3}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 3 * 120,
                          borderRight: "none",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <HomeIcon fontSize="small" />
                          Pag-IBIG
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff (EE)
                      </TableCell>
                      {/* <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff (ER)
                      </TableCell> */}
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff (EE)
                      </TableCell>
                      {/* <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff (ER)
                      </TableCell> */}
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total (EE)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total (ER)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff (EE)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff (ER)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff (EE)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff (ER)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total (EE)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total (ER)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff
                      </TableCell>
                      <TableCell
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 120,
                          borderRight: "none",
                        }}
                      >
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={20} align="center">
                          No Data Available
                        </TableCell>
                      </TableRow>
                    ) : (
                      <AnimatePresence>
                        {paginatedRows.map((row, index) => {
                          const isEvenRow = index % 2 === 0;
                          const sss = row.sss || {};
                          const ph = row.philhealth || {};
                          const pi = row.pagibig || {};

                          return (
                            <motion.tr
                              key={row.store}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              transition={{
                                duration: 0.2,
                                delay: index * 0.03,
                              }}
                              style={{
                                display: "table-row",
                                backgroundColor: isEvenRow
                                  ? "#ffffff"
                                  : "#f8f9fa",
                              }}
                              sx={{
                                "&:hover": {
                                  backgroundColor: isEvenRow
                                    ? "#f0f0f0 !important"
                                    : "#e9ecef !important",
                                  transform: "scale(1.005)",
                                  transition: "all 0.2s",
                                },
                                "& > .MuiTableCell-root": {
                                  borderRight: "1px solid #e0e0e0",
                                  borderBottom: "1px solid #e0e0e0",
                                },
                                "& > .MuiTableCell-root:last-child": {
                                  borderRight: "none",
                                },
                              }}
                            >
                              <TableCell sx={{ minWidth: 150 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  <Tooltip title="View Personnel">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<VisibilityIcon />}
                                      onClick={() =>
                                        handleViewPersonnel(row.store)
                                      }
                                      sx={{ minWidth: 120 }}
                                    >
                                      Personnel
                                    </Button>
                                  </Tooltip>

                                  <Tooltip title="Export Store">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<DownloadIcon />}
                                      onClick={() =>
                                        handleExportStore(row.store)
                                      }
                                      sx={{ minWidth: 120 }}
                                    >
                                      Store
                                    </Button>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ minWidth: 160 }}>
                                {row.store}
                              </TableCell>
                              <TableCell align="center" sx={{ minWidth: 140 }}>
                                {row.personnel_count}
                              </TableCell>
                              <TableCell align="center" sx={{ minWidth: 160 }}>
                                {row.first_date_range}
                              </TableCell>
                              <TableCell align="center" sx={{ minWidth: 160 }}>
                                {row.second_date_range}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.cutoff1)}
                              </TableCell>
                              {/* <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.cutoff1_er)}
                              </TableCell> */}
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.cutoff2)}
                              </TableCell>
                              {/* <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.cutoff2_er)}
                              </TableCell> */}
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.overall)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.overall_er)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(ph.cutoff1)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(ph.cutoff1_er)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(ph.cutoff2)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(ph.cutoff2_er)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(ph.overall)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(ph.overall_er)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(pi.cutoff1)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(pi.cutoff2)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(pi.overall)}
                              </TableCell>
                            </motion.tr>
                          );
                        })}
                      </AnimatePresence>
                    )}
                    {rows.length > 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          align="right"
                          sx={{ fontWeight: "bold" }}
                        >
                          Total:
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.sss_cutoff1)}
                        </TableCell>
                        {/* <TableCell align="right">
                          {formatNumber(totals.sss_cutoff1_er)}
                        </TableCell> */}
                        <TableCell align="right">
                          {formatNumber(totals.sss_cutoff2)}
                        </TableCell>
                        {/* <TableCell align="right">
                          {formatNumber(totals.sss_cutoff2_er)}
                        </TableCell> */}
                        <TableCell align="right">
                          {formatNumber(totals.sss_overall)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.sss_overall_er)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.ph_cutoff1)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.ph_cutoff1_er)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.ph_cutoff2)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.ph_cutoff2_er)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.ph_overall)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.ph_overall_er)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.pi_cutoff1)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.pi_cutoff2)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.pi_overall)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: "1px solid #e5e7eb",
                  "& .MuiTablePagination-actions": {
                    color: "#12C1DC",
                  },
                  "& .MuiTablePagination-selectIcon": {
                    color: "#12C1DC",
                  },
                }}
              />
            </Box>
          </Paper>
        </motion.div>
        <MbsModeDialog
          open={mbsModeDialogOpen}
          onClose={closeMbsModeDialog}
          onSelect={handleMbsModeSelect}
        />
      </Box>
    </Fade>
  );
};

export default StoreBenefits;
